import { useFindMany } from "@gadgetinc/react";
import { PlusIcon } from "@heroicons/react/16/solid";
import posthog from "posthog-js";
import React, { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api";
import { createScenarioFromScratch } from "../functions/createScenarioFromScratch";
import { CustomerContext } from "../providers";
import LoadingSpinner from "./LoadingSpinner";
import { OverflowFrame } from "./OverflowFrame";
import { PageHeading } from "./PageHeading";
import { Text } from "./tailwind/text";
import { DialogLibraryFlow } from "./DialogLibraryFlow";
import { GRADIENT_OPTIONS } from "../enums/gradient-backgrounds.enum";

export function CampaignTemplate({ hasAutomations = false }) {
  const [embedding] = useState();

  const navigate = useNavigate();
  const [{ data: dataLibraryFlow, fetching: fetchingLibraryFlow }] =
    useFindMany(api.libraryFlow, {
      select: {
        id: true,
        title: true,
        description: true,
        fullDescription: true,
        isAutomation: true,
        automationType: true,
        flowImageUrl: true,
        category: true,
        originalScenario: {
          id: true,
          name: true,
          campaign: {
            id: true,
            whatsappTemplate: {
              id: true,
              whatsappTemplateName: true,
              headerParameters: true,
              bodyParameters: true,
              urlClicks: true,
              redirectionUrl: {
                url: true,
              },
            },
          },
        },
        requirements: true,
        tags: true,
        thumbnailUrl: true,
        position: true,
        isPromoted: true,
        createdAt: true,
      },
      filter: {
        isAutomation: { equals: hasAutomations },
        isActive: { notEquals: false },
      },
      sort: {
        position: "Ascending",
      },
    });

  return (
    <>
      {fetchingLibraryFlow && !embedding && <LoadingSpinner />}
      {!fetchingLibraryFlow &&
        (!dataLibraryFlow || !dataLibraryFlow.length) &&
        (hasAutomations
          ? navigate("/automations/new/from-scratch")
          : navigate("/campaigns/new/from-scratch"))}
      {!fetchingLibraryFlow && dataLibraryFlow?.length > 0 && (
        <PageMarkup
          dataLibraryFlow={dataLibraryFlow}
          hasAutomations={hasAutomations}
        />
      )}
    </>
  );
}

function PageMarkup({ dataLibraryFlow, hasAutomations }) {
  const navigate = useNavigate();
  const { customer } = useContext(CustomerContext);

  const [active, setActive] = useState(false);
  const [selectedLibraryFlow, setSelectedLibraryFlow] = useState(
    dataLibraryFlow[0],
  );
  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const [campaignName, setCampaignName] = useState("");

  const handleClick = (libraryFlow) => {
    setSelectedLibraryFlow(libraryFlow);
    setCampaignName(libraryFlow?.originalScenario?.name ?? "");
    toggleActive();
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setCampaignName(value);
  };

  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      <PageHeading
        title={`New ${hasAutomations ? "Automation" : "Campaign"}`}
        iconPrimaryAction={<PlusIcon className="h-[1px]" />}
        primaryAction="Create from scratch"
        isPrimaryLoading={isLoading}
        isPrimaryDisabled={!customer.isSubscribed}
        handlePrimaryAction={async () => {
          setIsLoading(true);
          posthog.capture("create_flow", {
            source: "from-scratch",
            type: hasAutomations ? "automation" : "campaign",
          });
          if (hasAutomations) {
            navigate("/automations/new/from-scratch");
          } else {
            let nextPagePath = await createScenarioFromScratch({
              customerId: customer.id,
            });
            navigate(nextPagePath);
          }
        }}
      />

      <OverflowFrame>
        <br />
        {customer.isSubscribed ? (
          <div className="grid grid-cols-1 mb-4 gap-4 w-full justify-items-center sm:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 xxl:grid-cols-5">
            {dataLibraryFlow.map((libraryFlow) => (
              <CampaignCard
                key={libraryFlow.id}
                libraryFlow={libraryFlow}
                id={libraryFlow.id}
                title={libraryFlow.title}
                thumbnail={libraryFlow.thumbnailUrl}
                description={libraryFlow.description}
                isAutomation={libraryFlow.isAutomation}
                setSelectedLibraryFlow={setSelectedLibraryFlow}
                toggleActive={toggleActive}
                setCampaignName={setCampaignName}
                isPromoted={libraryFlow.isPromoted}
              />
            ))}
          </div>
        ) : null}

        <DialogLibraryFlow
          active={active}
          toggleActive={toggleActive}
          selectedLibraryFlow={selectedLibraryFlow}
          campaignName={campaignName}
          handleNameChange={handleNameChange}
          hasAutomations={hasAutomations}
        />
      </OverflowFrame>
    </>
  );

  function CampaignCard({
    libraryFlow,
    id,
    title,
    thumbnail,
    description,
    isAutomation,
  }) {
    const aMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    // Génération aléatoire du gradient
    const randomIndex = Math.floor(Math.random() * GRADIENT_OPTIONS.length);
    const randomGradient = GRADIENT_OPTIONS[randomIndex];

    return (
      <div
        key={id}
        onClick={() => handleClick(libraryFlow)}
        className="rounded-customBorderRadius border shadow-shadowbuttonmin hover:shadow-shadowbuttonpink hover:cursor-pointer overflow-hidden w-full relative"
      >
        <div className="w-full h-[190px] relative">
          <div
            className="w-full h-full"
            style={{
              background: randomGradient,
            }}
          ></div>
          <img
            src={thumbnail}
            alt={`Illustrative image of ${
              isAutomation ? "the automation" : "the campaign"
            } ${title}`}
            className="w-full h-[190px] object-cover absolute top-0 left-0 right-0"
          />
        </div>

        {libraryFlow.createdAt > aMonthAgo && (
          <>
            <div className="rotating-text-circle absolute top-0 right-0">
              <svg viewBox="0 0 120 120" className="w-12 h-12">
                <defs>
                  <path
                    id="circle"
                    d="M 60,60 m -30,0 a 30,30 0 1,1 60,0 a 30,30 0 1,1 -60,0"
                  />
                </defs>
                <text className="text-[26px] font-bold fill-red-600">
                  <textPath href="#circle">NEW! • NEW! •</textPath>
                </text>
              </svg>
            </div>
          </>
        )}
        <div className="p-3 h-[8rem]">
          <Text className="text-lg font-bold  leading-6">{title}</Text>
          <Text className="text-sm h-12 leading-6 mt-1">{description}</Text>
        </div>
      </div>
    );
  }
}
